/** The only place that talks to the backend.
 *
 *  Same-origin by default (`/api/v1`), so the refresh cookie stays
 *  SameSite=Strict: Caddy serves the SPA and proxies /api/* to the API on one
 *  origin. VITE_API_BASE exists only for the split-origin case.
 */
import type { paths } from './schema'

export const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

/** The error envelope every endpoint returns. Switch on `code`, never on prose. */
export interface ApiErrorBody {
  error: {
    code: string
    message: string
    details?: Record<string, unknown>
    request_id?: string
  }
}

export type SignedOff = {
  user_id: string; name: string; closed_at: string; remarks: string
}

export class ApiError extends Error {
  code: string
  status: number
  details: Record<string, unknown>
  requestId: string

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    const e = body?.error
    super(e?.message ?? fallback)
    this.code = e?.code ?? 'INTERNAL_ERROR'
    this.status = status
    this.details = (e?.details ?? {}) as Record<string, unknown>
    this.requestId = e?.request_id ?? ''
  }

  /** A stale write, not a failure: the UI offers a merge rather than an apology. */
  get isConflict() { return this.code === 'TASK_VERSION_CONFLICT' }
  get isDayClosed() { return this.code === 'DAY_CLOSED' }
  /** Somebody on this has signed off for the day. Not a failure - a question, which
   *  is why it has its own code: `DAY_CLOSED` means ask an Owner to reopen the day,
   *  and this one the person reopens themselves and the assigner may override. */
  get isPersonDayClosed() { return this.code === 'PERSON_DAY_CLOSED' }
  /** Who has signed off, when, and what they said - enough to render the warning
   *  without a second request. Their remark is the point: it is what the assigner
   *  actually decides with. */
  get signedOff(): SignedOff[] {
    const c = this.details?.closed
    return Array.isArray(c) ? (c as SignedOff[]) : []
  }
  get isAuth() { return this.status === 401 }
  get isForbidden() { return this.status === 403 }
  /** Field-level problems, for rendering next to inputs. */
  get fieldErrors(): { field: string; problem: string }[] {
    const f = this.details?.fields
    return Array.isArray(f) ? (f as { field: string; problem: string }[]) : []
  }
}

let accessToken: string | null = null
let onAuthLost: (() => void) | null = null

/** Held in memory only. localStorage would be XSS-exfiltratable, and the
 *  HttpOnly refresh cookie is what survives a reload. */
export const setAccessToken = (t: string | null) => { accessToken = t }
export const getAccessToken = () => accessToken
export const setAuthLostHandler = (fn: () => void) => { onAuthLost = fn }

let refreshing: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  // Single-flight: a burst of 401s must trigger one refresh, not one each.
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const r = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST', credentials: 'include',
        })
        if (!r.ok) return false
        const body = await r.json()
        accessToken = body.access_token
        return true
      } catch {
        return false
      } finally {
        setTimeout(() => { refreshing = null }, 0)
      }
    })()
  }
  return refreshing
}

interface RequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Set on mutations so a retried request cannot apply twice. */
  idempotencyKey?: string
  _retried?: boolean
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey

  const res = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  })

  if (res.status === 401 && !opts._retried) {
    // The access token is short-lived by design; renew silently and retry once.
    if (await refreshOnce()) return request<T>(path, { ...opts, _retried: true })
    onAuthLost?.()
  }

  if (!res.ok) {
    let body: ApiErrorBody | null = null
    try { body = await res.json() } catch { /* non-JSON error page */ }
    throw new ApiError(res.status, body, res.statusText || 'Request failed')
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** Fetch a file and hand it to the browser as a download.
 *
 *  Not `request()`: that always ends in res.json(). And not a plain <a href> either,
 *  because the access token lives in memory and a link would not carry it - the
 *  request would come back 401 and the user would get an error page in a new tab.
 *  So: fetch with the header, wrap the bytes in an object URL, click it, revoke.
 */
export async function download(path: string): Promise<void> {
  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  let res = await fetch(`${API_BASE}${path}`, { headers, credentials: 'include' })
  if (res.status === 401 && await refreshOnce()) {
    const retry: Record<string, string> = {}
    if (accessToken) retry.Authorization = `Bearer ${accessToken}`
    res = await fetch(`${API_BASE}${path}`, { headers: retry, credentials: 'include' })
  }
  if (!res.ok) {
    let body: ApiErrorBody | null = null
    try { body = await res.json() } catch { /* the error page is not JSON */ }
    throw new ApiError(res.status, body, res.statusText || 'Download failed')
  }

  // Prefer the filename the server chose; it carries the date range.
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const named = /filename="?([^"]+)"?/.exec(disposition)
  const url = URL.createObjectURL(await res.blob())
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = named?.[1] ?? path.split('/').pop() ?? 'download'
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)      // or the blob is held for the life of the tab
  }
}

/** Fetch a file and return an object URL for it, for showing rather than saving.
 *
 *  Same reason `download()` exists — the access token lives in memory, so a plain
 *  `<img src>` arrives unauthenticated and 401s. The difference is only what happens
 *  to the blob: this hands back a URL to put in an `<img>` or an `<iframe>`.
 *
 *  **The caller MUST revoke it**, or the blob is held for the life of the tab. Every
 *  caller here does it in a `useEffect` cleanup.
 */
export async function objectUrl(path: string): Promise<string> {
  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  let res = await fetch(`${API_BASE}${path}`, { headers, credentials: 'include' })
  if (res.status === 401 && await refreshOnce()) {
    const retry: Record<string, string> = {}
    if (accessToken) retry.Authorization = `Bearer ${accessToken}`
    res = await fetch(`${API_BASE}${path}`, { headers: retry, credentials: 'include' })
  }
  if (!res.ok) {
    let body: ApiErrorBody | null = null
    try { body = await res.json() } catch { /* the error page is not JSON */ }
    throw new ApiError(res.status, body, res.statusText || 'Could not load the file')
  }
  return URL.createObjectURL(await res.blob())
}

/** Upload one file as multipart. Not `request()`: that JSON-encodes the body, and
 *  setting Content-Type by hand would break the multipart boundary. */
export async function upload<T>(path: string, file: File,
                                caption = '', documentId = ''): Promise<T> {
  const form = new FormData()
  form.append('file', file)
  if (caption) form.append('caption', caption)
  // Which of the documents the task was asked for this file answers. Blank is fine:
  // a task can carry evidence nobody asked for.
  if (documentId) form.append('document_id', documentId)

  const send = async () => {
    const headers: Record<string, string> = {}
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`
    // Deliberately no Content-Type: fetch sets it, with the boundary.
    return fetch(`${API_BASE}${path}`,
                 { method: 'POST', headers, body: form, credentials: 'include' })
  }
  let res = await send()
  if (res.status === 401 && await refreshOnce()) res = await send()
  if (!res.ok) {
    let body: ApiErrorBody | null = null
    try { body = await res.json() } catch { /* not JSON */ }
    throw new ApiError(res.status, body, res.statusText || 'Upload failed')
  }
  return res.json() as Promise<T>
}

export const api = {
  get: <T>(p: string, signal?: AbortSignal) => request<T>(p, { signal }),
  post: <T>(p: string, body?: unknown, key?: string) =>
    request<T>(p, { method: 'POST', body, idempotencyKey: key }),
  patch: <T>(p: string, body?: unknown) => request<T>(p, { method: 'PATCH', body }),
  put: <T>(p: string, body?: unknown) => request<T>(p, { method: 'PUT', body }),
  del: <T>(p: string) => request<T>(p, { method: 'DELETE' }),
  download,
}

// ---- shapes lifted from the generated schema, so a backend change breaks the build
export type Task = paths['/api/v1/tasks']['get']['responses']['200']['content']['application/json'][number]
export type TaskEvent = paths['/api/v1/tasks/{task_id}/history']['get']['responses']['200']['content']['application/json'][number]
/** One line of a task's conversation. A question is a message with `needs_answer`
 *  set - that flag is the only thing separating the two, which is why there is one
 *  type here and not a Comment and a Query. */
export type ThreadMessage = paths['/api/v1/tasks/{task_id}/thread']['get']['responses']['200']['content']['application/json'][number]
/** Master data. `is_superuser` is the one account that always counts as an owner and
 *  cannot be deleted or locked out; `is_owner` already reflects that, so a screen
 *  should read it rather than looking for OWNER in `roles`. */
export type Person = {
  user_id: string; name: string; email: string; phone: string; roles: string[]
  active: boolean; whatsapp_opt_in: boolean; notifiable: boolean
  opt_in_at: string; why_not_notifiable: string; notes: string
  telegram_chat_id: string; telegram_linked_at: string; channel: string
  is_superuser: boolean; is_owner: boolean
  /** The reporting tree, and whether they may write for it. */
  reports_to: string; can_enter_tasks: boolean; direct_reports: number
  /** Approvers the owner named for them. Empty means "work it out from the tree". */
  approver_ids: string[]
}
/** One problem with an imported row: which cell, what it said, and why it did not
 *  resolve. `did_you_mean` is offered only for a close miss - a confident wrong
 *  suggestion is worse than none. */
export type RowProblem = {
  field: string
  value?: string
  message: string
  did_you_mean?: string
}

/** An imported row. `line` is the SPREADSHEET's row number, so a problem points at
 *  something the reader can see in Excel rather than at a grid position. */
export type ImportedRow = {
  line: number
  task: string
  company: string
  responsible_ids: string[]
  approver_ids: string[]
  responsible_rule: string
  approver_rule: string
  priority: string
  date: string
  end_date: string
  documents: { name: string; required: boolean }[]
  problems: RowProblem[]
}

/** What came back from a sheet. `skipped` is the rows that were not tasks at all;
 *  `truncated` says the file was longer than the 200 that can be filed at once. */
export type DraftImport = {
  read: number
  truncated: boolean
  rows: ImportedRow[]
  skipped: { line: number; message: string }[]
}

export type Company = {
  company_id: string; name: string; short_name: string; active: boolean
  default_approver_user_id: string; sort_order: number
}
export type Holiday = { date: string; name: string; type: string }
/** What is already coming to a day, before the Owner adds anything: rows that are
 *  really there, and a dry-run forecast of what the recurrence rules will add. The
 *  two never describe the same task — once generated, it moves from one to the other. */
export type DayPlan = {
  day: string; is_working_day: boolean; is_closed: boolean
  /** Where the day's work would go instead, when this day is not a working one.
   *  Null on a working day. */
  next_working_day: string | null
  calendar_warning: string | null
  existing: {
    ref: number; task: string; company: string; responsible: string; approver: string
    priority: string; status: string; carried: boolean; carry_forward_count: number
    from_rule: string
  }[]
  carried_in: number[]
  from_rules: {
    rule_ref: string; task: string; date: string; occurrence: string; moved: boolean
  }[]
  invalid_rules: { ref: string; problems: string[] }[]
}
export type Invite = {
  user_id: string; name: string; code: string; link: string
  expires_at: string; expires_in_days: number
}
export type Me = {
  /** The zone the firm's dates are in. Everything the API reads as a
   *  calendar date has to be computed in this, not in the browser's own. */
  timezone: string
  user_id: string; name: string; email: string; roles: string[]; is_owner: boolean
  /** The one account the alerts are sent to. Not the same as `is_owner`: there are
   *  several owners, and the System screen is not all of their business. */
  is_superuser: boolean
  /** Somebody reports to them. Enough to SEE their team's work, on its own. */
  is_manager: boolean
  /** ...and the Owner has switched task entry on for them. Both are needed: the
   *  switch alone grants nothing, because there is no team to write for. */
  may_enter_tasks: boolean
  permissions: Record<string, boolean>
}
/** Who the signed-in person may put on a task. Names and roles only - deliberately
 *  not `Person`, which carries the firm's contact book and stays owner-only. */
export type Assignee = {
  user_id: string; name: string; roles: string[]; active: boolean; is_owner: boolean
  /** Only when `GET /people?day=` was asked. Marks, never filters: a person who has
   *  signed off is still a legitimate choice, and hiding the name would take the
   *  decision away - a manager needs it precisely because he can reopen her day. */
  day_closed?: boolean
  day_remarks?: string
  /** Whether a message would actually reach them, and if not, why - in the server's
   *  own words, so this and Master data cannot say two different things about one
   *  person. Derived from the contact details and never the details themselves: this
   *  endpoint answers "who may I assign work to" without becoming the firm's address
   *  book. Marks, never filters, for the same reason `day_closed` does. */
  notifiable?: boolean
  why_not_notifiable?: string
}
/** One recorded problem. `delivered` is the field that matters on the screen: false
 *  means the Telegram message never went out, which is exactly the case the screen
 *  exists to cover - otherwise a broken bot and a quiet day look identical. */
export type Incident = {
  alert_id: string; day: string; category: string; key: string
  severity: 'immediate' | 'daily'
  summary: string; detail: string; count: number
  first_seen: string; last_seen: string
  notified_at: string; delivered: boolean; state: string
}
export type IncidentCount = { open: number; urgent: number }
/** One person's own sign-off. A different thing from `is_closed` below, which is the
 *  firm's day: this one freezes nothing and only stops NEW work reaching them. */
export type DayClose = {
  user_id: string; name: string; status: 'CLOSED' | 'OPEN'
  closed_at: string; closed_by: string; remarks: string; early: boolean
  mine: boolean; may_change: boolean
  outstanding: { open_tasks: number; awaiting: number; total: number }
}
export type DaySummary = {
  day: string; is_working_day: boolean; is_closed: boolean
  counters: { total: number; completed: number; pending_approval: number; rework: number; open: number; cancelled: number; carried_in: number }
  companies: string[]; unassigned: number[]; calendar_warning: string | null
  /** Scoped like the counters: your own row always, your team's if you have one,
   *  everybody's for an Owner. Reopened rows are included - "signed off at 16:10,
   *  back at 18:00" is the useful sentence. */
  closes: DayClose[]
}
export type QueueRow = {
  task: Task; waiting_days: number; overdue: boolean
  /** "mine" when you are named on it, "team" when you are covering for somebody
   *  beneath you. The server decides it, so the web and both bots agree. */
  whose: 'mine' | 'team'
}
export type CarryPlan = {
  target: string
  moving: { ref: number; task: string; company: string; responsible: string; carried: number }[]
  escalating: number[]; warnings: string[]
  /** Movers that were dated *before* this day — work the sweep found stranded on a
   *  day nothing ever came back for, rather than work this day is handing on. */
  stranded: { ref: number; task: string; was_dated: string; responsible: string }[]
}
/** What came back from dropping several tasks at once.
 *
 *  Three lists, not a count. Each task is decided on its own merits, so the honest
 *  answer names the ones that did not go and why - somebody may have been working
 *  from a screen that went stale while they read it. `told` is who was messaged,
 *  which is worth showing because dropping work is something other people find out
 *  about.
 */
export type DropResult = {
  cancelled: { task_id: string; ref: number; task: string }[]
  skipped: { task_id: string; ref: number | null; why: string }[]
  told: { user_id: string; name: string; tasks: number }[]
}
export type Check = { level: string; code: string; message: string }
export type Rule = {
  recurrence_id: string; ref: string; active: boolean; company: string; task: string
  frequency: string; interval: number; day_of_month: string | null
  non_working_day_rule: string; start_date: string; end_date: string
  // Everything else the editor round-trips. It prefills from `GET /recurrences` and
  // sends the whole rule back, so a field absent here is one it would blank.
  priority: string; days_of_week: string; months_of_year: string; remarks: string
  responsible_person: string; approver: string; generated_count: number
  valid: boolean; problems: string[]; row_version: number
}
export type PreviewRow = { occurrence: string; resolved: string; moved: boolean; skipped: boolean; task: string }
/** What `POST /days/{day}/open` reports back. `will_carry_in` is the future-day case:
 *  nothing moved yet, because tonight's carry-forward is the right moment. */
export type OpenedDay = {
  day: string
  marked_working: boolean
  will_carry_in: boolean
  moved: { ref: number; task: string; company: string; responsible: string
           was_dated: string }[]
  caught_up?: Record<string, Record<string, unknown>>
}
/** One thing a task is expected to arrive with. `required` blocks submission until
 *  it is provided; an optional one is a checklist. */
export type TaskDocument = {
  document_id: string
  name: string
  required: boolean
  attachment_id: string
  provided: boolean
}
export type CalendarDay = { date: string; working: boolean; kind: string; name: string }
export type CalendarYear = {
  year: number; working_days: string; saturday_working_ordinals: string
  sunday_working_ordinals: string; configured_through: string
  warning: string | null; days: CalendarDay[]
}
export type Report = {
  range: { from: string; to: string }
  totals: { tasks: number; completed: number; completion_rate: number }
  by_person: Record<string, { total: number; completed: number; pending: number; rework: number; open: number; rate: number }>
  by_company: Record<string, { total: number; completed: number; pending: number; rework: number; open: number; rate: number }>
  ageing: { ref: number; task: string; company: string; responsible: string; carried: number }[]
  /** Raised against completed per day, including the quiet days — a gap in the
   *  series would make a slow week look like a short one. Counted from each task's
   *  own date, so it always adds up to `totals` above. */
  trend: { date: string; raised: number; completed: number }[]
  /** How long work waits with each approver. Approvals never carry forward, so this
   *  is the only place a slow queue shows. */
  approvals: Record<string, {
    decided: number; avg_hours: number; worst_hours: number; waiting_now: number
  }>
  /** What completion rate cannot say: finished on the day it was due, and how often
   *  it came back. */
  quality: Record<string, {
    total: number; on_time: number; reworked: number
    on_time_rate: number; rework_rate: number
  }>
  /** Name → id, per breakdown. The panels are keyed by name because that is what a
   *  person reads; the task list filters by id. This is what lets a figure link to
   *  the tasks behind it without matching on names. */
  /** How many tasks the viewer can see that this range excludes. A range ending
   *  today is right for a completion rate, but it hides the list somebody just wrote
   *  for tomorrow — and a bare "0 of 0" reads as "there is nothing". */
  outside_range: number
  ids: {
    person: Record<string, string>
    approver: Record<string, string>
    company: Record<string, string>
  }
}

/** Open work as it stands right now. Its own endpoint, and its own type, because it
 *  is a snapshot and every figure in `Report` is scoped to a date range. */
export type Workload = {
  as_of: string
  total: number
  median_load: number
  /** Keyed by **user_id**, not by name - two people can share a name and used to
   *  share one figure. `(unassigned)` is the one key that is not an id. */
  by_person: Record<string, {
    name: string
    open: number; urgent: number; important: number; regular: number; carried: number
    waiting_to_approve: number; load: number; signal: string; why: string
  }>
  /** The server's own scoring constants. The rail applies them to rows that exist
   *  only in the browser, so it has to be these numbers and not a copy of them. */
  weights: {
    priority: Record<string, number>
    stuck_after: number
    over: number
    under: number
    busy_floor: number
  }
}
